/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useState } from "react";
import {
  DragDropContext,
  DragStart,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import { Modal } from "@nordcloud/gnui";
import { getDroppableCss } from "~/components/Styled";
import { reorder, useComponentsSelector } from "~/hooks";
import { isEmpty, isNotEmpty, isNotNil } from "~/tools";
import {
  isBaselinePatchingEnabled,
  isPatchOrScanEntity,
  getBaselineSourceIndex,
  getFlatPlanEntities,
  updateBaselineSourceParameter,
} from "~/utils";
import { baselineStrings } from "~/views/plans/constants";
import { PlanField } from "~/views/plans/PlanCreate/components/PlanCreateWizard/constants";
import { usePlanWizard } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanProvider";
import { useStepState } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/StepProvider";
import { PlanEntity } from "../../../types";
import { DraggablePlanEntitiesList } from "./DraggablePlanEntitiesList";

type Props = {
  entities: PlanEntity[];
  nextStep?: () => void;
};

export function DraggablePlanEntities({ entities, nextStep }: Props) {
  const {
    items,
    handleDragEnd: handleDragEnding,
    handleDelete,
    handleShowSearch,
  } = useComponentsSelector([]);
  const { updateStepState } = useStepState();
  const { setPlanData } = usePlanWizard();
  const [showBaselineDragWarningModal, setShowBaselineDragWarningModal] =
    useState(false);
  const [draggedEntityIndex, setDraggedEntityIndex] = useState(-1);

  useEffect(() => {
    if (entities) {
      handleShowSearch(entities);
    }
  }, [entities, handleShowSearch]);

  const setNewPlanEntities = (newEntities: PlanEntity[]) => {
    setPlanData((prevPlanData) => ({
      ...prevPlanData,
      [PlanField.PLAN_SETTINGS]: {
        planEntities: newEntities,
      },
    }));
  };

  const isBaselineDraggingDisabled = (dropPositionIndex?: number) => {
    if (!isBaselinePatchingEnabled(entities) || draggedEntityIndex === -1) {
      return false;
    }

    const currentEntities = entities ?? [];
    const draggedEntity = currentEntities[draggedEntityIndex];

    const isDraggedEntityPatchOrScan =
      isPatchOrScanEntity(draggedEntity) ||
      !!draggedEntity?.batchActions?.some((entity) =>
        isPatchOrScanEntity(entity)
      );

    if (!isDraggedEntityPatchOrScan) {
      return false;
    }

    const baselineEntityIndex = getBaselineSourceIndex(
      getFlatPlanEntities(currentEntities)
    );

    if (baselineEntityIndex !== -1) {
      const isMovingBaselineAction = baselineEntityIndex === draggedEntityIndex;
      const isMovingFromAboveBaselineSourceToBelow =
        dropPositionIndex !== undefined &&
        dropPositionIndex >= baselineEntityIndex &&
        draggedEntityIndex < baselineEntityIndex;

      const isMovingFromBelowBaselineSourceToAbove =
        dropPositionIndex !== undefined &&
        dropPositionIndex <= baselineEntityIndex &&
        draggedEntityIndex > baselineEntityIndex;

      return (
        isMovingBaselineAction ||
        isMovingFromAboveBaselineSourceToBelow ||
        isMovingFromBelowBaselineSourceToAbove
      );
    }

    return false;
  };

  const disableBaselinePatching = () => {
    const updatedEntities = entities.map((entity) => {
      const updatedEntity = updateBaselineSourceParameter(entity);

      if (
        isNotNil(updatedEntity.batchActions) &&
        isNotEmpty(updatedEntity.batchActions)
      ) {
        const updatedBatchActions = updatedEntity.batchActions.map(
          (batchAction) => updateBaselineSourceParameter(batchAction)
        );

        return {
          ...updatedEntity,
          batchActions: updatedBatchActions,
        };
      }
      return updatedEntity;
    });

    updateStepState({
      entities: updatedEntities,
    });

    setNewPlanEntities(updatedEntities);
  };

  const handleDragStart = (start: DragStart) => {
    setDraggedEntityIndex(start.source.index);
  };

  const handleDragEnd = (result: DropResult) => {
    const currentEntities = entities ?? [];

    if (isBaselineDraggingDisabled(result.destination?.index)) {
      setShowBaselineDragWarningModal(true);
      return;
    }

    handleDragEnding(result);
    const newEntities = reorder(
      currentEntities,
      result.source.index,
      result.destination?.index ?? -1
    );

    updateStepState({
      entities: newEntities,
    });

    setNewPlanEntities(newEntities);
  };

  const handleDeleteEntity = (item: PlanEntity) => {
    handleDelete(item);
  };

  if (isEmpty(items.SELECTED_ITEMS)) {
    return null;
  }

  return (
    <>
      <Modal
        isOpen={showBaselineDragWarningModal}
        onClose={() => setShowBaselineDragWarningModal(false)}
        contentLabel="Baseline Action"
        actions={[
          {
            label: "Cancel",
            onAction: () => {
              setShowBaselineDragWarningModal(false);
            },
            severity: "low",
            order: 1,
          },
          {
            label: "OK",
            onAction: () => {
              setShowBaselineDragWarningModal(false);
              disableBaselinePatching();
            },
            severity: "high",
            order: 2,
          },
        ]}
      >
        {baselineStrings.cannotDragModalWarning}
      </Modal>

      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <Droppable droppableId="SELECTED_ITEMS">
          {(provided, snapshot) => {
            return (
              <div
                ref={provided.innerRef}
                css={getDroppableCss(true, snapshot.isDraggingOver)}
              >
                <DraggablePlanEntitiesList
                  items={items["SELECTED_ITEMS"]}
                  onDelete={handleDeleteEntity}
                  nextStep={nextStep}
                />
                {provided.placeholder}
              </div>
            );
          }}
        </Droppable>
      </DragDropContext>
    </>
  );
}
